import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    Settings, Plus, Save, RefreshCw, Loader2, X,
    Search, Edit2, Trash2, Copy, Check,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConfigItem {
    key: string;
    value: unknown;
    updated_at: string | null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function GeneralConfigPage() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [editDialog, setEditDialog] = useState<{ open: boolean; item: ConfigItem | null }>({ open: false, item: null });
    const [form, setForm] = useState({ key: '', value: '' });
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [deleteConfigKey, setDeleteConfigKey] = useState<string | null>(null);

    // ─── Load Config ───────────────────────────
    const { data: configItems = [], isLoading } = useQuery({
        queryKey: ['admin', 'app-config-all'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<ConfigItem[]>('admin_get_all_config');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const filteredItems = searchQuery
        ? configItems.filter(c =>
            c.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            JSON.stringify(c.value).toLowerCase().includes(searchQuery.toLowerCase())
        )
        : configItems;

    // ─── Mutations ─────────────────────────────
    const saveMutation = useMutation({
        mutationFn: async (data: { key: string; value: string; isNew: boolean }) => {
            let parsedValue: unknown;
            try {
                parsedValue = JSON.parse(data.value);
            } catch {
                parsedValue = data.value; // String fallback
            }

            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: data.key,
                p_value: parsedValue,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] });
            setEditDialog({ open: false, item: null });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (key: string) => {
            const { error } = await dbClient.rpc('admin_delete_app_config', { p_key: key });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] });
        },
    });

    const openDialog = (item: ConfigItem | null) => {
        setForm({
            key: item?.key || '',
            value: item ? JSON.stringify(item.value, null, 2) : '',
        });
        setEditDialog({ open: true, item });
    };

    const copyValue = (key: string, value: unknown) => {
        navigator.clipboard.writeText(JSON.stringify(value, null, 2));
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const getValuePreview = (value: unknown): string => {
        if (typeof value === 'string') return `"${value}"`;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (value === null) return 'null';
        return JSON.stringify(value);
    };

    const getValueType = (value: unknown): string => {
        if (value === null) return 'null';
        if (Array.isArray(value)) return 'array';
        return typeof value;
    };

    const TYPE_COLORS: Record<string, string> = {
        string: 'text-emerald-400',
        number: 'text-blue-400',
        boolean: 'text-amber-400',
        object: 'text-purple-400',
        array: 'text-cyan-400',
        null: 'text-zinc-400',
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Configuración General"
                description={`${configItems.length} claves de configuración — AppConfig key-value store`}
            />

            {/* Toolbar */}
            <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar por clave o valor..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
                <button
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] })}
                    className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                    <RefreshCw size={14} />
                </button>
                <button
                    onClick={() => openDialog(null)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                    <Plus size={14} />
                    Nueva Clave
                </button>
            </div>

            {/* Config Items */}
            {isLoading ? (
                <div className="py-12 flex items-center justify-center">
                    <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                    {searchQuery ? 'No se encontraron claves.' : 'No hay claves de configuración.'}
                </div>
            ) : (
                <div className="space-y-1">
                    {filteredItems.map(item => {
                        const valueType = getValueType(item.value);
                        const valuePreview = getValuePreview(item.value);
                        const isTruncated = valuePreview.length > 120;

                        return (
                            <div
                                key={item.key}
                                className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:border-border transition-colors group"
                            >
                                {/* Key + Type */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-foreground font-mono">{item.key}</span>
                                        <span className={cn('text-[10px] font-mono', TYPE_COLORS[valueType] || 'text-muted-foreground')}>
                                            {valueType}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
                                        {isTruncated ? valuePreview.slice(0, 120) + '…' : valuePreview}
                                    </p>
                                    {item.updated_at && (
                                        <p className="text-[10px] text-muted-foreground mt-1 opacity-60">
                                            Actualizado: {new Date(item.updated_at).toLocaleString('es-ES')}
                                        </p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                    <button
                                        onClick={() => copyValue(item.key, item.value)}
                                        className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                        title="Copiar valor"
                                    >
                                        {copiedKey === item.key ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                    </button>
                                    <button
                                        onClick={() => openDialog(item)}
                                        className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                        title="Editar"
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfigKey(item.key)}
                                        className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-400"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ─── Edit Dialog ──────────────────────────────────────────── */}
            {editDialog.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditDialog({ open: false, item: null })}>
                    <div className="w-full max-w-lg mx-4 rounded-xl border border-border bg-card p-6 space-y-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-foreground">
                                {editDialog.item ? `Editar: ${editDialog.item.key}` : 'Nueva Clave de Configuración'}
                            </h3>
                            <button onClick={() => setEditDialog({ open: false, item: null })} className="text-muted-foreground hover:text-foreground">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Clave *</label>
                                <input
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
                                    placeholder="mi_clave_config"
                                    value={form.key}
                                    onChange={e => setForm(p => ({ ...p, key: e.target.value }))}
                                    disabled={!!editDialog.item}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Valor (JSON o String) *</label>
                                <textarea
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-xs font-mono resize-none"
                                    placeholder={'{\n  "ejemplo": "valor"\n}'}
                                    rows={8}
                                    value={form.value}
                                    onChange={e => setForm(p => ({ ...p, value: e.target.value }))}
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Si el valor es JSON válido, se almacenará como objeto. Si no, se guardará como string.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setEditDialog({ open: false, item: null })}
                                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => saveMutation.mutate({
                                    key: form.key,
                                    value: form.value,
                                    isNew: !editDialog.item,
                                })}
                                disabled={!form.key || !form.value || saveMutation.isPending}
                                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                                {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AdminConfirmDialog
                open={!!deleteConfigKey}
                variant="danger"
                title="Eliminar clave"
                description={
                    deleteConfigKey ? (
                        <p>
                            ¿Eliminar la clave <span className="font-mono text-foreground">{deleteConfigKey}</span>?
                        </p>
                    ) : null
                }
                confirmLabel="Eliminar"
                onCancel={() => setDeleteConfigKey(null)}
                onConfirm={() => {
                    if (deleteConfigKey) {
                        deleteMutation.mutate(deleteConfigKey);
                        setDeleteConfigKey(null);
                    }
                }}
                confirmDisabled={deleteMutation.isPending}
            />
        </div>
    );
}
